import Vue from 'vue';
import Vuex from 'vuex';
import VueRouter from 'vue-router';
import './registerServiceWorker';
import AppConfig from '@/config';
import LocalStorage from '@/services/LocalStorage';
import globalStore from './store/index';
// Mixins

// Modules
import ui from '@/modules/ui/index';
import business from '@/modules/business/index';
import account from '@/modules/account/index';
import publicModule from '@/modules/public/index';

import redirects from '@/router/redirects';

// Vue Select
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

// Vue Select customized for AOL UI
import AolSelect from '@/ui/atoms/AolSelect.vue';

// Form Validate
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import rules from '@/utils/ValidateRules';

// Mask
import { mask } from 'vue-the-mask';
import VueClipboard from 'vue-clipboard2';
import VueLoading from '@/plugins/Loader';

// Authorization
import UserService from '@/services/AccountHttp';

// filters
import dateFilters from '@/modules/business/common/date.filter';

import App from './App.vue';
import VueI18n from 'vue-i18n';

// Import file languages
import en from './locales/en.json';
import pt from './locales/pt-BR.json';
import es from './locales/es.json';

Vue.config.productionTip = false;

Vue.use(VueI18n);

// i18n configuration with individually imported messages
const i18n = new VueI18n({
  locale: navigator.language || 'pt-BR',
  fallbackLocale: 'pt-BR',
  messages: {
    'pt-BR': pt,
    en: en,
    es,
  },
});

// Load the language from LocalStorage or fallback to the browser's language
const storedLanguage = LocalStorage.get('language');
const browserLanguage = navigator.language;
const defaultLanguage = process.env.VUE_APP_I18N_LOCALE ?? 'pt-BR';
i18n.locale = storedLanguage || browserLanguage || defaultLanguage;

import VueGtm from '@gtm-support/vue2-gtm';
import LanguageSwitcher from '@/modules/translate/LanguageSwitcher.vue';

const modules = [ui, publicModule, business, account, redirects];

Vue.config.productionTip = false;

Vue.use(Vuex);
Vue.use(VueRouter);
Vue.use(VueClipboard);
Vue.use(VueLoading, { text: i18n.t('loadingText') });
Vue.filter('date', dateFilters.dateNormal);
Vue.filter('dateTime', dateFilters.dateTime);
Vue.component('v-select', vSelect);
Vue.component('aol-select', AolSelect);
Vue.component('ValidationObserver', ValidationObserver);
Vue.component('ValidationProvider', ValidationProvider);
Vue.directive('mask', mask);
Vue.component('LanguageSwitcher', LanguageSwitcher);

modules.push({
  name: 'globalStore',
  store: globalStore,
  routes: [],
});

const config = modules.reduce(
  (list, item) => {
    const ret = list;
    ret.routes = ret.routes.concat(item.routes);
    ret.store = Object.assign(ret.store, item.store);
    return ret;
  },
  { routes: [], store: [], nav: [] }
);

Vue.directive('tooltip', (el, binding) => {
  window.jQuery(el).tooltip({
    title: binding.value,
    placement: binding.arg,
    trigger: 'hover',
    html: true,
  });
});

Object.keys(rules).forEach((rule) => {
  extend(rule, rules[rule]);
});

Vue.filter('fileSize', (bytes) => {
  if (bytes < 1025) {
    return `${bytes.toFixed(1)} Bytes`;
  }
  const kBytes = bytes / 1024;
  if (kBytes < 1025) {
    return `${kBytes.toFixed(1)} Kb`;
  }
  const mBytes = kBytes / 1024;
  return `${mBytes.toFixed(1)} Mb`;
});

Vue.filter('date', (stringDate) => {
  const date = new Date(stringDate);
  return date.toLocaleDateString(i18n.locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
});

Vue.filter('time', (stringDate) => {
  const date = new Date(stringDate);
  return date.toLocaleString(i18n.locale, {
    hour: 'numeric',
    minute: 'numeric',
  });
});

Vue.filter('dateTime', (stringDate) => {
  const date = new Date(stringDate);
  return date.toLocaleDateString(i18n.locale, {
    dateStyle: 'short',
    timeZone: 'UTC',
  });
});

Vue.filter('utc-date', (stringDate) => {
  const date = new Date(stringDate);
  return date.toLocaleDateString(i18n.locale, {
    dateStyle: 'short',
    timeZone: 'UTC',
  });
});

Vue.filter('fileExtensions', (extensions) => {
  return extensions.reduce((ac, v, index) => {
    ac = ac + `${v}`;
    if (index + 1 < extensions.length - 1) {
      ac = ac + ', ';
    }
    if (index + 1 === extensions.length - 1) {
      ac = ac + ' ou ';
    }
    return ac;
  }, '');
});

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: config.routes,
  linkActiveClass: 'active',
  linkExactActiveClass: 'active',
});

const vuexStore = new Vuex.Store({
  modules: config.store,
});

router.beforeEach((to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAdmin)) {
    const isAdmin = vuexStore.getters['organizations/getIsOwner'];
    if (!isAdmin) {
      return next({ name: 'account.profile' });
    }
    next();
  } else {
    next();
  }
});

if (AppConfig.isGTMEnabled) {
  Vue.use(VueGtm, {
    id: AppConfig.GTMCode,
    defer: true,
    compatibility: false,
    nonce: Math.random().toString(36),
    enabled: AppConfig.isGTMEnabled,
    debug: false,
    loadScript: true,
    vueRouter: router,
    ignoredViews: [],
    trackOnNextTick: true,
  });
}

new Vue({
  async created() {
    this.$loading(true);
    this.isMobile = this.isMobileChecker();
    this.isIos = this.isIosChecker();
    this.isMobileScreen = this.isMobileScreenChecker();
    if (this.$route.name.split('.').shift() === 'public') {
      this.loaded = true;
      return;
    }
    window.addEventListener('resize', () => {
      this.isMobileScreen = this.isMobileScreenChecker();
    });
    let routeToRedirect = null;
    try {
      const result = await UserService.setErrorHandler((error) => {
        throw error;
      }).getUser();
      this.user = result.data;
      this.isUSAVersion = result.data.is_usa_version ?? false;

      //Set the language in the user object
      try {
        const userLanguage = this.user?.lang || defaultLanguage;
        this.currentLanguage = userLanguage;
        this.$i18n.locale = userLanguage;
        LocalStorage.set('language', userLanguage);
        if (userLanguage !== i18n.locale) {
          window.location.reload();
        }
      } catch (error) {
        console.error('Failed to fetch user language from backend:', error);
        this.currentLanguage = defaultLanguage;
        this.$i18n.locale = defaultLanguage;
      }

      if (window.location.pathname === '/') {
        // Go to dashboard page
        routeToRedirect = { name: 'business.dashboard' };
      }
    } catch (error) {
      if (
        error.response?.status === 401 &&
        this.$route.query.action === 'show_recipient_action'
      ) {
        // Go to envelope public page
        routeToRedirect = {
          name: 'public.envelopes.viewer',
          query: this.$route.query.token,
        };
        return;
      }
      if (
        error.response?.status === 401 &&
        this.$route.query.action === 'show_recipient_envelope_completed'
      ) {
        // Go to download public page
        routeToRedirect = {
          name: 'public.envelopes.download',
          params: {
            token: this.$route.query.token,
            envelope: this.$route.query.envelope || undefined,
          },
        };
        return;
      }
      if (error.response?.status === 401 || error.response?.status === 412) {
        // Go to Authorization Server
        window.location.assign(
          `${AppConfig.authorizationServer}?continue=${window.location}`
        );
      }
    } finally {
      this.loaded = true;
      this.closeSideBarAfterClickRoute();
      if (routeToRedirect) {
        this.$router.replace(routeToRedirect);
      }
    }
  },
  data: {
    user: {
      name: '',
      avatar: '',
    },
    loaded: false,
    isMobile: false,
    isIos: false,
    isMobileScreen: false,
    iconsCache: {},
    isUSAVersion: false,
    warningOverlapPercent: 0.5,
  },
  methods: {
    async logout() {
      const result = await UserService.logout();
      if (result.status === 204) {
        LocalStorage.set('organization', false);
        window.location.assign(AppConfig.loginPath);
      }
    },
    isMobileChecker() {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      ) {
        return true;
      }
      return false;
    },
    isIosChecker() {
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        return true;
      }
      return false;
    },
    isMobileScreenChecker() {
      return window.innerWidth <= 768;
    },
    closeSideBarAfterClickRoute() {
      this.$router.beforeEach((to, from, next) => {
        window.jQuery('#close-sidebar').click();
        next();
      });
    },
    closeSidebar() {
      window.document.getElementById('sidebar').classList.remove('show');
      window.document
        .getElementById('backdrop')
        .classList.remove('modal-backdrop');
      window.document.getElementById('backdrop').classList.remove('show');
    },
  },
  i18n, // Make sure to pass the i18n here
  router,
  store: vuexStore,
  render: (h) => h(App),
}).$mount('#app');
